import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Layers, ChevronRight, FileText, ChevronDown } from 'lucide-react';
import api from '../../utils/api';

const LearnMegaMenu = ({
    triggerClassName = "flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-smooth text-slate-600 hover:text-slate-900 hover:bg-slate-100",
    iconColor,
    hideIcon = false
}) => {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [rootCategories, setRootCategories] = useState([]);
    const [activeRoot, setActiveRoot] = useState(null);
    const [activeDetail, setActiveDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const [activeSub, setActiveSub] = useState(null);
    const [subDetail, setSubDetail] = useState(null);
    const [loadingSub, setLoadingSub] = useState(false);

    // Fetch roots on mount
    useEffect(() => {
        const fetchRoots = async () => {
            try {
                const res = await api.get('/api/learn/categories');
                setRootCategories(res.data || []);
            } catch (err) {
                console.error("Failed to fetch learn roots:", err);
            }
        };
        fetchRoots();
    }, []);

    // Fetch detail when hovering a root category
    useEffect(() => {
        if (!activeRoot) return;

        const fetchDetail = async () => {
            setLoadingDetail(true);
            setActiveSub(null);
            setSubDetail(null);
            try {
                const res = await api.get(`/api/learn/categories/${activeRoot.slug}`);
                if (activeRoot.slug === res.data.category.slug) {
                    setActiveDetail(res.data);
                }
            } catch (err) {
                console.error("Failed to fetch detail for", activeRoot.slug, err);
            } finally {
                setLoadingDetail(false);
            }
        };

        fetchDetail();
    }, [activeRoot]);

    // Fetch subcategory articles when hovering a subcategory
    useEffect(() => {
        if (!activeSub) return;

        const fetchSubDetail = async () => {
            setLoadingSub(true);
            try {
                // console.log("Fetching articles for subcategory:", activeSub.slug);
                const res = await api.get(`/api/learn/categories/${activeSub.slug}`);
                if (res.data && res.data.articles) {
                    setSubDetail(res.data);
                }
            } catch (err) {
                console.error("Failed to fetch sub-detail for", activeSub.slug, err);
            } finally {
                setLoadingSub(false);
            }
        };

        const timer = setTimeout(fetchSubDetail, 100); // Reduced delay
        return () => clearTimeout(timer);
    }, [activeSub]);

    // Handle hover delays
    let closeTimeout;
    const handleMouseEnterMenu = () => {
        clearTimeout(closeTimeout);
        setIsOpen(true);
    };
    const handleMouseLeaveMenu = () => {
        closeTimeout = setTimeout(() => {
            setIsOpen(false);
            setActiveRoot(null);
            setActiveDetail(null);
            setActiveSub(null);
            setSubDetail(null);
        }, 200);
    };

    return (
        <div
            className="relative flex items-center h-full"
            onMouseEnter={handleMouseEnterMenu}
            onMouseLeave={handleMouseLeaveMenu}
        >
            <button
                onClick={() => navigate('/learn')}
                className={triggerClassName}
            >
                {!hideIcon && <BookOpen size={18} className={iconColor} />}
                <span>Learn</span>
                <ChevronDown size={14} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* The Mega Menu Dropdown */}
            {isOpen && (
                <div
                    className="absolute top-[100%] left-0 z-[200] pt-2"
                    style={{ width: '680px', transform: 'translateX(-15%)' }} // Narrower menu, adjusted centering
                >
                    <div className="bg-white dark:bg-card rounded-2xl border border-slate-200 dark:border-border shadow-[0_20px_40px_-15px_rgba(0,0,0,0.1)] dark:shadow-none overflow-hidden flex h-[480px]">

                        {/* Left Column: Root Categories (30% width) */}
                        <div className="w-[30%] bg-slate-50 dark:bg-muted/30 border-r border-slate-200 dark:border-border flex flex-col shrink-0">
                            <div className="p-4 pb-2">
                                <h3 className="text-[10px] font-bold text-slate-400 dark:text-slate-500tracking-[0.15em] mb-3 px-2">
                                    Learning Paths
                                </h3>
                            </div>
                            <div className="flex-1 overflow-y-auto px-2 pb-4 custom-scrollbar-mini">
                                <ul className="space-y-0.5">
                                    {rootCategories.map(cat => (
                                        <li key={cat.id}>
                                            <button
                                                onMouseEnter={() => setActiveRoot(cat)}
                                                onClick={() => {
                                                    navigate(`/learn/${cat.slug}`);
                                                    setIsOpen(false);
                                                }}
                                                className={`w-full flex items-center justify-between p-2.5 rounded-lg transition-all text-[13px] font-bold
                            ${activeRoot?.id === cat.id
                                                         ? 'bg-white dark:bg-card text-emerald-700 dark:text-primary shadow-sm border border-emerald-100 dark:border-primary/20'
                                                         : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200/50 dark:hover:bg-muted border border-transparent'
                                                     }
                           `}
                                            >
                                                <span className="truncate">{cat.name}</span>
                                                <ChevronRight size={14} className={activeRoot?.id === cat.id ? 'text-emerald-500 dark:text-primary' : 'text-slate-300 dark:text-slate-600'} />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="p-4 pt-2 border-t border-slate-100 dark:border-border bg-slate-50/80 dark:bg-muted/80 backdrop-blur-sm">
                                <Link
                                    to="/learn"
                                    onClick={() => setIsOpen(false)}
                                    className="text-[11px] font-bold text-emerald-600 dark:text-primary hover:text-emerald-700 dark:hover:text-primary/95 flex items-center gap-1 group"
                                >
                                    View All Paths <ChevronRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                                </Link>
                            </div>
                        </div>

                        {/* Right Column: Topics & Articles (70% width) */}
                        <div className="w-[70%] bg-white dark:bg-card flex flex-col overflow-hidden">
                            {!activeRoot ? (
                                <div className="h-full flex flex-col items-center justify-center text-slate-400 dark:text-slate-500 p-6">
                                    <BookOpen size={48} className="mb-4 text-slate-100 dark:text-slate-800" />
                                    <p className="font-bold text-slate-500 dark:text-slate-400">Master New Skills</p>
                                    <p className="text-xs text-slate-400 dark:text-slate-500 text-center max-w-[200px] mt-1">Hover over a learning path to explore structured topics and expert articles.</p>
                                </div>
                            ) : loadingDetail && !activeDetail ? (
                                <div className="h-full flex items-center justify-center">
                                    <div className="w-6 h-6 border-2 border-slate-100 dark:border-border border-t-emerald-600 dark:border-t-primary rounded-full animate-spin" />
                                </div>
                            ) : activeDetail ? (
                                <div className="flex flex-col h-full overflow-hidden">
                                    {/* Header */}
                                    <div className="p-5 pb-4 border-b border-slate-50 dark:border-border flex items-center justify-between bg-white dark:bg-card shrink-0">
                                        <div className="min-w-0 flex-1 pr-4">
                                            <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 tracking-tight truncate">{activeRoot.name}</h2>
                                            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1 font-medium">{activeRoot.description || 'Explore modules and master concepts.'}</p>
                                        </div>
                                        <button
                                            onClick={() => {
                                                navigate(`/learn/${activeRoot.slug}`);
                                                setIsOpen(false);
                                            }}
                                            className="px-3 py-1.5 bg-emerald-50 dark:bg-primary/10 text-emerald-700 dark:text-primary text-[11px] font-blacktracking-wider rounded-md hover:bg-emerald-100 dark:hover:bg-primary/20 transition-colors shrink-0"
                                        >
                                            View Full Path
                                        </button>
                                    </div>

                                    {/* Content Scroll Area */}
                                    <div className="flex-1 overflow-y-auto p-5 custom-scrollbar-mini">
                                        <div className="grid grid-cols-2 gap-x-8">
                                            {/* Subcategories (Topics) */}
                                            <div className="flex flex-col gap-3">
                                                <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500tracking-[0.2em] flex items-center gap-1.5">
                                                    <Layers size={14} className="text-emerald-500/70 dark:text-primary/70" /> Topics
                                                </span>
                                                <div className="space-y-0.5">
                                                    {activeDetail.child_categories && activeDetail.child_categories.length > 0 ? (
                                                        activeDetail.child_categories.map(child => (
                                                            <div
                                                                key={child.id}
                                                                onMouseEnter={() => {
                                                                    if (!activeSub || activeSub.id !== child.id) {
                                                                        setActiveSub(child);
                                                                        setSubDetail(null);
                                                                    }
                                                                }}
                                                                className={`group flex items-center justify-between text-[13px] py-2 px-2.5 rounded-lg transition-all cursor-default w-full
                                                                    ${activeSub?.id === child.id
                                                                        ? 'bg-emerald-50 dark:bg-primary/10 text-emerald-800 dark:text-primary shadow-sm border border-emerald-100/50 dark:border-primary/20'
                                                                        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-muted hover:text-emerald-700 dark:hover:text-primary border border-transparent'}
                                                                `}
                                                            >
                                                                <Link
                                                                    to={`/learn/${child.slug}`}
                                                                    onClick={() => setIsOpen(false)}
                                                                    className="flex-1 truncate font-bold"
                                                                >
                                                                    {child.name}
                                                                </Link>
                                                                <ChevronRight size={14} className={`transition-all duration-200 ${activeSub?.id === child.id ? 'translate-x-0.5 text-emerald-500 dark:text-primary opacity-100' : 'text-slate-300 dark:text-slate-600 opacity-0 group-hover:opacity-100'}`} />
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="text-[11px] text-slate-400 dark:text-slate-500 italic py-2">No subtopics available.</div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Articles */}
                                            <div className="flex flex-col gap-3 border-l border-slate-100 dark:border-border pl-8">
                                                <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500tracking-[0.2em] flex items-center gap-1.5">
                                                    <FileText size={14} className="text-emerald-500/70 dark:text-primary/70" />
                                                    {activeSub ? `Articles: ${activeSub.name}` : 'Recommended'}
                                                </span>
                                                <div className="space-y-4 relative">
                                                    {loadingSub ? (
                                                        <div className="py-8 flex items-center justify-center">
                                                            <div className="w-5 h-5 border-2 border-slate-100 dark:border-border border-t-emerald-600 dark:border-t-primary rounded-full animate-spin" />
                                                        </div>
                                                    ) : (
                                                        ((activeSub ? subDetail?.articles : activeDetail.articles) || []).length > 0 ? (
                                                            ((activeSub ? subDetail?.articles : activeDetail.articles) || []).map(article => (
                                                                <Link
                                                                    key={article.id}
                                                                    to={`/learn/${activeRoot.slug}/${article.slug}`}
                                                                    onClick={() => setIsOpen(false)}
                                                                    className="block group"
                                                                >
                                                                    <div className="text-[13px] font-bold text-slate-800 dark:text-slate-200 group-hover:text-emerald-600 dark:group-hover:text-primary transition-colors line-clamp-1 mb-0.5">{article.title}</div>
                                                                    <div className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-1 font-medium">{article.summary || article.title}</div>
                                                                </Link>
                                                            ))
                                                        ) : (
                                                            <div className="text-[11px] text-slate-400 dark:text-slate-500 py-4 italic">No articles found here.</div>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LearnMegaMenu;
